import type { Page } from '@namu/protocol'
import { ApiError } from './client'
import type { ApiClient, Query } from './client'
import type { AuditEvent, Instant } from './types'

/**
 * Who did what, and who looked at whom.
 *
 * Read-only, and there is no method here that writes or deletes one. That is
 * the design: audit events are written by the server for every mutation and for
 * every read of an individual record, and they are not editable through any
 * route. A console method to remove one would be a hole in the only record that
 * can answer whether a person's transcript was opened, and by whom.
 *
 * Reads being logged as well as writes is the unusual half and the important
 * one. "Who opened this person's case" is a protection question, and a log that
 * records only changes cannot answer it.
 *
 * The list pages by cursor like everything else, and here the reason is at its
 * sharpest: an offset page over a log that is being written to as it is read
 * silently omits rows, and an audit export that is missing an access is an
 * export taken to prove something that it no longer proves.
 */

export interface AuditListQuery extends Query {
  actorUserId?: string
  action?: string
  subjectType?: string
  subjectId?: string
  from?: Instant
  to?: Instant
  limit?: number
  cursor?: string
}

/**
 * The two date bounds, renamed to what the route actually validates.
 *
 * `GET /v1/org/audit` reads `since` and `until`. The console has always sent
 * `from` and `to`, and this is the quietest divergence in the whole API layer,
 * because an unrecognised query parameter is dropped rather than refused: the
 * request succeeded, the server returned the unfiltered log, and the screen
 * presented it under the heading of a date range nobody had applied. Somebody
 * checking whether a record was opened last March was reading every access
 * there has ever been and had no way to tell.
 *
 * Renamed here rather than in the screen, because the console's own vocabulary
 * for a date range is `from` and `to` on six screens and one filter object; the
 * wire's is `since` and `until`. One translation at the boundary is better than
 * either half changing its mind.
 */
function withWireDates(query: AuditListQuery): Query {
  const { from, to, ...rest } = query
  return {
    ...rest,
    ...(from === undefined ? {} : { since: from }),
    ...(to === undefined ? {} : { until: to }),
  }
}

/**
 * An audit event exactly as `/v1/org/audit` sends one.
 *
 * `actorLabel` is the field name, and the console reads `actorName`. The two
 * are not synonyms and the server's choice is the deliberate one: it stores an
 * email address rather than a display name, because two people called Amina is
 * ordinary and this row has to say which one, and it keeps the address beside
 * the id precisely because the id is nulled when that member of staff is
 * deleted, which is exactly when somebody is reading these rows.
 *
 * Before this mapping existed the console read `actorName` off a payload that
 * has never carried one, so every row fell through to the roster lookup and a
 * leaver, whose id is null and whose whole purpose the label serves, rendered
 * as the words "The platform". A protection log attributing a person's actions
 * to the system is the one thing it must not do.
 */
interface AuditEventWire {
  id?: unknown
  actorUserId?: unknown
  actorLabel?: unknown
  action?: unknown
  subjectType?: unknown
  subjectId?: unknown
  requestId?: unknown
  metadata?: unknown
  at?: unknown
}

/**
 * Reads one row, defensively, and never throws on a field that is not there.
 *
 * Typed as `unknown` per field rather than as the shape it is meant to be,
 * because this table is the one place in the console where a crash is worse
 * than a blank: the whole point of the screen is to be able to answer, after
 * the fact, whether somebody's record was opened, and a component that dies on
 * a malformed row answers nothing at all. A row with a missing action reads as
 * "unrecorded" and the rest of the page still draws.
 */
function toAuditEvent(wire: AuditEventWire): AuditEvent {
  return {
    id: text(wire.id) ?? `${text(wire.at) ?? ''}:${text(wire.action) ?? ''}`,
    actorUserId: text(wire.actorUserId),
    actorName: text(wire.actorLabel),
    action: text(wire.action) ?? 'unrecorded',
    subjectType: text(wire.subjectType) ?? 'unknown',
    subjectId: text(wire.subjectId),
    requestId: text(wire.requestId),
    metadata:
      typeof wire.metadata === 'object' && wire.metadata !== null && !Array.isArray(wire.metadata)
        ? (wire.metadata as Record<string, unknown>)
        : null,
    at: text(wire.at) ?? '',
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * The `items` array, whatever came back where it should have been.
 *
 * A list route answers `{ items, nextCursor? }` and a caller that hands the
 * envelope itself to something expecting an array gets "object is not
 * iterable", which is how three other screens in this console died. The defence
 * is one step further out: a 200 whose `items` is not an array is caught here
 * rather than becoming a thrown iterator inside a table.
 *
 * Caught, and then reported rather than swallowed. This used to answer with an
 * empty page, which is the one wrong answer this particular screen can give: an
 * audit log exists to say whether somebody's record was opened, and a blank one
 * is read as "nobody opened it". That is the opposite of what an unreadable
 * response means, and it is the reading somebody would take into a
 * conversation with the person whose record it is.
 *
 * This is not in tension with the note above `toAuditEvent`, which says a
 * malformed row must not kill the table, and still holds: a row missing its
 * action still renders as "unrecorded" beside every row that read cleanly. What
 * changes here is only the case where there is no array of rows at all.
 */
function readItems(value: unknown): AuditEventWire[] {
  if (!Array.isArray(value)) {
    throw new ApiError({
      status: 200,
      code: 'internal',
      message: 'Namu sent an answer this console could not read.',
      recovery: 'retry',
    })
  }
  return value as AuditEventWire[]
}

export function auditApi(http: ApiClient) {
  return {
    async list(query: AuditListQuery = {}): Promise<Page<AuditEvent>> {
      const page = await http.list<unknown>('/v1/org/audit', withWireDates(query))
      return {
        items: readItems(page?.items).map(toAuditEvent),
        ...(typeof page?.nextCursor === 'string' ? { nextCursor: page.nextCursor } : {}),
      }
    },

    /**
     * Walks the whole filtered log.
     *
     * Returns `complete: false` rather than silently truncating when the filter
     * matches more than the ceiling, because a partial audit export that looks
     * whole is the specific failure this endpoint exists to avoid.
     */
    async collect(query: AuditListQuery = {}, max?: number) {
      const collected = await http.collect<AuditEventWire>(
        '/v1/org/audit',
        withWireDates(query),
        max === undefined ? {} : { max },
      )
      return { items: collected.items.map(toAuditEvent), complete: collected.complete }
    },
  }
}

export type AuditApi = ReturnType<typeof auditApi>
